/**
 * ChatTransport — framework-free strategy interface for receiving Parlant events.
 *
 * Implementations: SseTransport (preferred) and LongPollTransport (fallback).
 * The interface mirrors a minimal EventEmitter so the React layer only needs to
 * attach to well-known events.
 */

import type { ParlantEvent } from '../types/parlant';

// ---------------------------------------------------------------------------
// Event definitions
// ---------------------------------------------------------------------------

export type TransportName = 'sse' | 'polling';

export interface TransportMetricEvent {
  /** ttfc = time-to-first-chunk (ms), reconnect = reconnect attempt,
   *  demote = SSE → polling, promote = polling → SSE (re-attempt) */
  name: 'ttfc' | 'reconnect' | 'demote' | 'promote';
  value: number;
}

export interface ChatTransportEventMap {
  events: ParlantEvent[];
  error: string;
  transportChange: TransportName;
  metric: TransportMetricEvent;
}

export type ChatTransportEventType = keyof ChatTransportEventMap;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Listener<T = any> = (data: T) => void;

// ---------------------------------------------------------------------------
// ChatTransport interface
// ---------------------------------------------------------------------------

export interface ChatTransport {
  /**
   * Open the transport for the given session starting at `lastOffset`.
   * Replaces any previous subscription for the same session.
   */
  start(sessionId: string, lastOffset: number): void;

  /** Close all connections and cancel all timers. */
  stop(): void;

  on<K extends ChatTransportEventType>(
    event: K,
    listener: Listener<ChatTransportEventMap[K]>
  ): void;

  off<K extends ChatTransportEventType>(
    event: K,
    listener: Listener<ChatTransportEventMap[K]>
  ): void;
}

// ---------------------------------------------------------------------------
// Minimal EventEmitter shared by both implementations
// ---------------------------------------------------------------------------

export class BaseTransport implements ChatTransport {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _listeners: Map<string, Set<Listener<any>>> = new Map();

  on<K extends ChatTransportEventType>(
    event: K,
    listener: Listener<ChatTransportEventMap[K]>
  ): void {
    let set = this._listeners.get(event);
    if (!set) {
      set = new Set();
      this._listeners.set(event, set);
    }
    set.add(listener);
  }

  off<K extends ChatTransportEventType>(
    event: K,
    listener: Listener<ChatTransportEventMap[K]>
  ): void {
    this._listeners.get(event)?.delete(listener);
  }

  protected emit<K extends ChatTransportEventType>(
    event: K,
    data: ChatTransportEventMap[K]
  ): void {
    this._listeners.get(event)?.forEach(fn => fn(data));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  start(_sessionId: string, _lastOffset: number): void {
    throw new Error('start() not implemented');
  }

  stop(): void {
    throw new Error('stop() not implemented');
  }
}
